import { useEffect, useRef } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { Minus, Square, X, Copy } from "lucide-react";
import type { WindowState } from "@/types";
import { APPS } from "@/data/apps";
import { useHive } from "@/store/useHive";
import { clamp } from "@/lib/utils";
import { AppContent } from "@/components/apps/registry";

const BAR_H = 52;
const DOCK_H = 96;
const SPRING = { type: "spring", stiffness: 420, damping: 34, mass: 0.7 } as const;

export function Window({ win }: { win: WindowState }) {
  const def = APPS[win.appId];
  const focusWindow = useHive((s) => s.focusWindow);
  const closeWindow = useHive((s) => s.closeWindow);
  const minimizeWindow = useHive((s) => s.minimizeWindow);
  const toggleMaximize = useHive((s) => s.toggleMaximize);
  const moveWindow = useHive((s) => s.moveWindow);
  const resizeWindow = useHive((s) => s.resizeWindow);
  const topZ = useHive((s) => s.topZ);
  const isFocused = win.z === topZ;

  // Position & size are driven by motion values so a drag tracks the pointer
  // 1:1 — setting a motion value bypasses React re-render and the spring, so
  // the window doesn't rubber-band behind the cursor. External changes (open,
  // maximize, restore) are animated onto these values with the spring instead.
  const x = useMotionValue(win.x);
  const y = useMotionValue(win.y);
  const w = useMotionValue(win.w);
  const h = useMotionValue(win.h);
  const interacting = useRef(false);

  useEffect(() => {
    // Skip while the user is dragging/resizing: the motion values already
    // track the pointer, and re-springing to the store value would fight it.
    if (interacting.current) return;
    const controls = [
      animate(x, win.x, SPRING),
      animate(y, win.y, SPRING),
      animate(w, win.w, SPRING),
      animate(h, win.h, SPRING),
    ];
    return () => controls.forEach((c) => c.stop());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win.x, win.y, win.w, win.h]);

  const dragOrigin = useRef({ x: 0, y: 0, px: 0, py: 0 });
  function onTitlePointerDown(e: React.PointerEvent) {
    if (win.maximized) return;
    focusWindow(win.id);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    interacting.current = true;
    dragOrigin.current = { x: win.x, y: win.y, px: e.clientX, py: e.clientY };
  }
  function onTitlePointerMove(e: React.PointerEvent) {
    if (!interacting.current || !(e.buttons & 1) || win.maximized) return;
    const dx = e.clientX - dragOrigin.current.px;
    const dy = e.clientY - dragOrigin.current.py;
    x.set(clamp(dragOrigin.current.x + dx, -win.w + 120, window.innerWidth - 120));
    y.set(clamp(dragOrigin.current.y + dy, BAR_H + 4, window.innerHeight - DOCK_H - 20));
  }
  function endTitleDrag(e: React.PointerEvent) {
    if (!interacting.current) return;
    interacting.current = false;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    moveWindow(win.id, x.get(), y.get()); // commit final position to the store
  }

  const resizeRef = useRef({ w: 0, h: 0, px: 0, py: 0 });
  function onResizeDown(e: React.PointerEvent) {
    e.stopPropagation();
    focusWindow(win.id);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    interacting.current = true;
    resizeRef.current = { w: win.w, h: win.h, px: e.clientX, py: e.clientY };
  }
  function onResizeMove(e: React.PointerEvent) {
    if (!interacting.current || !(e.buttons & 1)) return;
    e.stopPropagation();
    const dw = e.clientX - resizeRef.current.px;
    const dh = e.clientY - resizeRef.current.py;
    w.set(clamp(resizeRef.current.w + dw, 420, window.innerWidth - win.x - 16));
    h.set(clamp(resizeRef.current.h + dh, 320, window.innerHeight - win.y - DOCK_H - 8));
  }
  function endResize(e: React.PointerEvent) {
    if (!interacting.current) return;
    interacting.current = false;
    e.stopPropagation();
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    resizeWindow(win.id, w.get(), h.get(), win.x, win.y);
  }

  return (
    <motion.div
      className="absolute flex flex-col overflow-hidden rounded-2xl border border-app-strong shadow-window"
      style={{ x, y, width: w, height: h, zIndex: win.z }}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.92 }}
      transition={SPRING}
      onPointerDown={() => focusWindow(win.id)}
    >
      {/* glass body */}
      <div className="glass-strong absolute inset-0 -z-10" />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${def.accent}66, transparent)` }}
      />

      {/* Title bar */}
      <div
        className="flex h-11 shrink-0 items-center gap-3 border-b border-app px-4 select-none"
        onPointerDown={onTitlePointerDown}
        onPointerMove={onTitlePointerMove}
        onPointerUp={endTitleDrag}
        onPointerCancel={endTitleDrag}
        onDoubleClick={() => toggleMaximize(win.id)}
        style={{ cursor: win.maximized ? "default" : "grab" }}
      >
        <div className="flex items-center gap-2 group">
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeWindow(win.id);
            }}
            className="grid h-3 w-3 place-items-center rounded-full bg-[#ff5f57] transition hover:brightness-110"
          >
            <X size={8} className="opacity-0 text-black/60 group-hover:opacity-100" strokeWidth={3} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              minimizeWindow(win.id);
            }}
            className="grid h-3 w-3 place-items-center rounded-full bg-[#febc2e] transition hover:brightness-110"
          >
            <Minus size={8} className="opacity-0 text-black/60 group-hover:opacity-100" strokeWidth={3} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleMaximize(win.id);
            }}
            className="grid h-3 w-3 place-items-center rounded-full bg-[#28c840] transition hover:brightness-110"
          >
            {win.maximized ? (
              <Copy size={7} className="opacity-0 text-black/60 group-hover:opacity-100" strokeWidth={3} />
            ) : (
              <Square size={7} className="opacity-0 text-black/60 group-hover:opacity-100" strokeWidth={3} />
            )}
          </button>
        </div>

        <div className="ml-1 flex items-center gap-2 text-xs font-medium text-app-1">
          <def.icon size={13} style={{ color: def.accent }} />
          <span className="text-app">{def.name}</span>
          <span className="text-app-2">— {def.tagline}</span>
        </div>

        <div
          className={`ml-auto h-1.5 w-1.5 rounded-full transition ${
            isFocused ? "" : "opacity-30"
          }`}
          style={{ background: def.accent }}
        />
      </div>

      {/* Content */}
      <div className="relative flex-1 overflow-auto no-scrollbar">
        <AppContent appId={win.appId} />
      </div>

      {/* Resize handle */}
      {!win.maximized && (
        <div
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={endResize}
          onPointerCancel={endResize}
          className="absolute bottom-0 right-0 z-10 h-5 w-5 cursor-nwse-resize"
          style={{
            background:
              "linear-gradient(135deg, transparent 50%, hsl(var(--border-strong)) 50%, hsl(var(--border-strong)) 60%, transparent 60%, transparent 70%, hsl(var(--border-strong)) 70%, hsl(var(--border-strong)) 80%, transparent 80%)",
          }}
        />
      )}
    </motion.div>
  );
}
